import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";

// Labels are "English name (native name)", as shown in the language dropdowns
export const LANGUAGES = [
  { code: "en", name: "English", native: "English" },
  { code: "hi", name: "Hindi", native: "हिन्दी" },
  { code: "ta", name: "Tamil", native: "தமிழ்" },
  { code: "te", name: "Telugu", native: "తెలుగు" },
  { code: "kn", name: "Kannada", native: "ಕನ್ನಡ" },
  { code: "ml", name: "Malayalam", native: "മലയാളം" },
  { code: "mr", name: "Marathi", native: "मराठी" },
];

// Kept when the user logs out (see navbar logout)
export const LANGUAGE_STORAGE_KEY = "app_language";
const DEFAULT_LANGUAGE = "en";

// English ships in the main bundle; other languages load as separate chunks on first use
const loaders = {
  hi: () => import(/* webpackChunkName: "lang-hi" */ "./locales/hi.json"),
  ta: () => import(/* webpackChunkName: "lang-ta" */ "./locales/ta.json"),
  te: () => import(/* webpackChunkName: "lang-te" */ "./locales/te.json"),
  kn: () => import(/* webpackChunkName: "lang-kn" */ "./locales/kn.json"),
  ml: () => import(/* webpackChunkName: "lang-ml" */ "./locales/ml.json"),
  mr: () => import(/* webpackChunkName: "lang-mr" */ "./locales/mr.json"),
};

const isSupported = (code) => LANGUAGES.some((language) => language.code === code);

const readSavedLanguage = () => {
  try {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    return isSupported(saved) ? saved : DEFAULT_LANGUAGE;
  } catch (error) {
    return DEFAULT_LANGUAGE;
  }
};

const ensureLoaded = async (code) => {
  if (code === DEFAULT_LANGUAGE || i18n.hasResourceBundle(code, "translation")) return;
  const module = await loaders[code]();
  i18n.addResourceBundle(code, "translation", module.default || module, true, true);
};

// Switches the whole app instantly (no reload) and remembers the choice
export const changeLanguage = async (code) => {
  if (!isSupported(code)) return;
  await ensureLoaded(code);
  await i18n.changeLanguage(code);
};

const rememberLanguage = (code) => {
  document.documentElement.lang = code;
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch (error) {
    // Storage can be unavailable (private mode); the choice then lasts for this visit only
  }
};

// Resolves once the saved language is ready, so the first render is already in that language
export const initI18n = async () => {
  // Read the saved choice before init: init itself announces "en", which must not overwrite it
  const saved = readSavedLanguage();
  await i18n.use(initReactI18next).init({
    resources: { en: { translation: en } },
    lng: DEFAULT_LANGUAGE,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: LANGUAGES.map((language) => language.code),
    interpolation: { escapeValue: false }, // React already escapes output
    returnNull: false,
  });
  i18n.on("languageChanged", rememberLanguage);
  if (saved !== DEFAULT_LANGUAGE) {
    try {
      await changeLanguage(saved);
    } catch (error) {
      // If the language file can't load (offline), stay in English
    }
  }
  document.documentElement.lang = i18n.language;
  return i18n;
};

export default i18n;
